'use client';

import { Content } from '@prismicio/client';

import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

import clsx from 'clsx';
import { EmblaCarouselType } from 'embla-carousel';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { CallToAction } from '@/app/_componentsv2/call-to-action';
import { Carousel as DefaultCarousel } from '@/app/_componentsv2/carousels/carousel';
import { CarouselNav } from '@/app/_componentsv2/carousels/carousel-nav';
import { InfiniteCarousel } from '@/app/_componentsv2/carousels/infinite-carousel';
import { IconBigArrowLeft } from '@/app/_componentsv2/icons/big-arrow-left';
import { IconBigArrowRight } from '@/app/_componentsv2/icons/big-arrow-right';

import { VideoPlayer } from '@/app/_componentsv2/media/video-player';
import { TabPanels } from '@/app/_componentsv2/tab-panels';
import { Tabs } from '@/app/_componentsv2/tabs';
import { CarouselSlicePersonsPrimarySlidesItem } from '@/prismicio-types';

import {
  carousels,
  companiesCarousel,
  companiesCarouselSlide,
  companiesCarouselSlideImg,
  companiesInnerWrapper,
  contentSlide,
  contentSlideContent,
  contentSlideDescription,
  contentSlideDescriptionCallToAction,
  contentSlideImg,
  contentSlides,
  contentsOuterWrapper,
  contentThumbnail,
  contentThumbnailImg,
  contentThumbnails,
  contentThumbnailSlides,
  defaultCarouselSlide,
  defaultCarouselSlideAfterAfterSelected,
  defaultCarouselSlideAfterSelected,
  defaultCarouselSlideBeforeBeforeSelected,
  defaultCarouselSlideDescription,
  defaultCarouselSlideImg,
  defaultCarouselSlideImgWrapper,
  defaultCarouselSlides,
  defaultCarouselSlideSelected,
  defaultCarouselSlideTitle,
  defaultCarouselSlideWrapper,
  defaultContent,
  defaultInnerWrapper,
  innerWrapper,
  outerWrapper,
  personGroup,
  personGroupCarousel,
  personGroups,
  personGroupSlides,
  personsInnerWrapper,
  personSlide,
  personSlideDescription,
  personSlideImg,
  personSlideTitle,
  selectedontentThumbnail,
  testimoniesDetails,
  testimoniesDetailsCarousel,
  testimoniesDetailsSlide,
  testimoniesDetailsSlidePerson,
  testimoniesDetailsSlidePersonTitle,
  testimoniesDetailsSlides,
  testimoniesDetailsSlideTestimony,
  testimoniesDetailsThumb,
  testimoniesDetailsThumbCarousel,
  testimoniesDetailsThumbCarouselButton,
  testimoniesDetailsThumbCarouselWrapper,
  testimoniesDetailsThumbImg,
  testimoniesDetailsThumbs,
  testimoniesDetailsThumbSelected,
  testimoniesInnerWrapper,
  testimoniesTitle,
  testimoniesVideos,
  testimoniesVideosSlide,
  testimoniesVideosSlides,
  title,
  verticalCarouselSlide,
  verticalCarouselSlideImg,
  verticalCarouselSlideLabel,
  verticalCaroussel,
  verticalCarousselMobile,
  verticalContent,
  verticalInnerWrapper,
} from './index.css';

/**
 * Props for `Carousel`.
 */
export type CarouselProps = SliceComponentProps<Content.CarouselSlice>;

/**
 * Component for "Carousel" Slices.
 */
const Carousel: FC<CarouselProps> = (props) => {
  const { slice } = props;

  if (slice.variation === 'companies') {
    return <CompaniesCarousel sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'vertical') {
    return <VerticalCarousels sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'persons') {
    return <PersonsCarousel sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'testimonies') {
    return <TestimoniesCarousel sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'default') {
    return <DefaultCarouselSlice sliceType={slice.slice_type} slice={slice} />;
  } else if (slice.variation === 'contents') {
    return <ContentsCarouselSlice sliceType={slice.slice_type} slice={slice} />;
  }
};

function ContentsCarouselSlice({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.CarouselSliceContents;
}) {
  const [selected, setSelected] = useState(0);

  const [thumbnailsEmblaApi, setThumbnailsEmblaApi] =
    useState<EmblaCarouselType | null>(null);
  const [contentsEmblaApi, setContentsEmblaApi] =
    useState<EmblaCarouselType | null>(null);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!thumbnailsEmblaApi || !contentsEmblaApi) {
        return;
      }
      contentsEmblaApi.scrollTo(index);
    },
    [thumbnailsEmblaApi, contentsEmblaApi],
  );

  const onSelect = useCallback(() => {
    if (!thumbnailsEmblaApi || !contentsEmblaApi) {
      return;
    }
    setSelected(contentsEmblaApi.selectedScrollSnap());
    thumbnailsEmblaApi.scrollTo(contentsEmblaApi.selectedScrollSnap());
  }, [contentsEmblaApi, thumbnailsEmblaApi, setSelected]);

  useEffect(() => {
    if (!contentsEmblaApi) {
      return;
    }
    onSelect();

    contentsEmblaApi.on('select', onSelect).on('reInit', onSelect);
  }, [onSelect, contentsEmblaApi]);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={clsx(outerWrapper, contentsOuterWrapper)}
    >
      <section className={innerWrapper}>
        <PrismicRichText
          components={{
            paragraph: ({ children }) => <h2>{children}</h2>,
          }}
          field={slice.primary.title}
        />
        <DefaultCarousel
          classNames={{
            carousel: contentThumbnails,
            slides: contentThumbnailSlides,
          }}
          options={{
            containScroll: 'keepSnaps',
            dragFree: true,
          }}
          emblaApiRef={setThumbnailsEmblaApi}
        >
          {slice.primary.slides.map((slide, index) => (
            <section
              key={index}
              className={clsx(contentThumbnail, {
                [selectedontentThumbnail]:
                  index === contentsEmblaApi?.selectedScrollSnap(),
              })}
              onClick={() => onThumbClick(index)}
            >
              <PrismicNextImage
                fallbackAlt=""
                className={contentThumbnailImg}
                field={slide.thumbnail}
              />
            </section>
          ))}
        </DefaultCarousel>
        <DefaultCarousel
          classNames={{
            slides: contentSlides,
          }}
          emblaApiRef={setContentsEmblaApi}
        >
          {slice.primary.slides.map((slide, index) => (
            <section key={index} className={contentSlide}>
              <PrismicNextImage
                fallbackAlt=""
                className={contentSlideImg}
                field={slide.image}
              />
              <section className={contentSlideContent}>
                <PrismicRichText
                  components={{
                    paragraph: ({ children }) => <h3>{children}</h3>,
                  }}
                  field={slide.title}
                />
                <section className={contentSlideDescription}>
                  <PrismicRichText
                    components={{
                      paragraph: ({ children }) => <p>{children}</p>,
                    }}
                    field={slide.description}
                  />
                </section>
                <CallToAction
                  className={contentSlideDescriptionCallToAction}
                  type="prismic"
                  field={slide.call_to_action}
                />
              </section>
            </section>
          ))}
        </DefaultCarousel>
        <CarouselNav
          bullets={slice.primary.slides.length}
          selected={selected}
          onSelect={onThumbClick}
          showBullets={false}
        />
      </section>
    </section>
  );
}

function DefaultCarouselSlice({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.CarouselSliceDefault;
}) {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (emblaApi === null) {
      return;
    }

    emblaApi.on('select', () => setSelected(emblaApi.selectedScrollSnap()));
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi === null) {
      return;
    }

    emblaApi.scrollTo(selected);
  }, [selected, emblaApi]);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={outerWrapper}
    >
      <section className={clsx(innerWrapper, defaultInnerWrapper)}>
        <PrismicRichText
          components={{
            paragraph: ({ children }) => <h2>{children}</h2>,
          }}
          field={slice.primary.title}
        />
        <section className={defaultContent}>
          <DefaultCarousel
            classNames={{ slides: defaultCarouselSlides }}
            emblaApiRef={setEmblaApi}
          >
            {slice.primary.slides.map((slide, index) => (
              <section
                key={index}
                className={clsx(defaultCarouselSlideWrapper, {
                  [defaultCarouselSlideSelected]: selected === index,
                  [defaultCarouselSlideBeforeBeforeSelected]:
                    index < selected - 1,
                  [defaultCarouselSlideAfterSelected]: index > selected,
                  [defaultCarouselSlideAfterAfterSelected]:
                    index > selected + 1,
                })}
              >
                <section className={clsx(defaultCarouselSlide)}>
                  <section
                    className={defaultCarouselSlideImgWrapper}
                    role="presentation"
                  >
                    <PrismicNextImage
                      fallbackAlt=""
                      className={defaultCarouselSlideImg}
                      field={slide.image}
                    />
                  </section>
                  <PrismicRichText
                    components={{
                      paragraph: ({ children }) => (
                        <span className={defaultCarouselSlideTitle}>
                          {children}
                        </span>
                      ),
                    }}
                    field={slide.title}
                  />
                  <PrismicRichText
                    components={{
                      paragraph: ({ children }) => (
                        <span className={defaultCarouselSlideDescription}>
                          {children}
                        </span>
                      ),
                    }}
                    field={slide.description}
                  />
                </section>
              </section>
            ))}
          </DefaultCarousel>
          <CarouselNav
            selected={selected}
            bullets={slice.primary.slides.length}
            onSelect={setSelected}
          />
        </section>
      </section>
    </section>
  );
}

function CompaniesCarousel({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.CarouselSliceCompanies;
}) {
  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={outerWrapper}
    >
      <section className={companiesInnerWrapper}>
        <PrismicRichText
          components={{
            paragraph: ({ children }) => <h2 className={title}>{children}</h2>,
          }}
          field={slice.primary.title}
        />
        <InfiniteCarousel
          classNames={{ wrapper: companiesCarousel }}
          direction="horizontal"
        >
          {slice.primary.slides.map((item, index) => (
            <section key={index} className={companiesCarouselSlide}>
              <PrismicNextImage
                fallbackAlt=""
                className={companiesCarouselSlideImg}
                field={item.image}
              />
            </section>
          ))}
        </InfiniteCarousel>
      </section>
    </section>
  );
}

function VerticalCarousels({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.CarouselSliceVertical;
}) {
  const slides = slice.primary.slides.map((slide, index) => (
    <section key={index} className={verticalCarouselSlide}>
      <PrismicNextImage
        fallbackAlt=""
        className={verticalCarouselSlideImg}
        field={slide.image}
      />
      <PrismicRichText
        components={{
          paragraph: ({ children }) => (
            <span className={verticalCarouselSlideLabel}>{children}</span>
          ),
        }}
        field={slide.title}
      />
    </section>
  ));

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={outerWrapper}
    >
      <section className={verticalInnerWrapper}>
        <section className={verticalContent}>
          <PrismicRichText
            components={{
              paragraph: ({ children }) => (
                <h2 className={title}>{children}</h2>
              ),
            }}
            field={slice.primary.title}
          />
          <PrismicRichText field={slice.primary.description} />
        </section>
        <section className={carousels}>
          <InfiniteCarousel
            classNames={{ wrapper: verticalCarousselMobile }}
            direction={'horizontal'}
          >
            {slides}
          </InfiniteCarousel>
          <InfiniteCarousel
            classNames={{ wrapper: verticalCaroussel }}
            direction={'vertical'}
          >
            {slides}
          </InfiniteCarousel>
          <InfiniteCarousel
            classNames={{ wrapper: verticalCaroussel }}
            direction={'vertical-reverse'}
          >
            {slides}
          </InfiniteCarousel>
        </section>
      </section>
    </section>
  );
}

function PersonsCarousel({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.CarouselSlicePersons;
}) {
  const groups = slice.primary.slides.reduce(
    (groups, slide) => {
      if (slide.group === null) {
        return groups;
      }

      let group = groups[slide.group];
      if (group === undefined) {
        group = [];
        groups[slide.group] = group;
      }

      group.push(slide);

      return groups;
    },
    {} as Record<string, CarouselSlicePersonsPrimarySlidesItem[]>,
  );

  const [selected, setSelected] = useState(Object.keys(groups)[0] ?? '');

  const [emblaApi, setEmblaApi] = useState<Record<string, EmblaCarouselType>>(
    {},
  );
  const [selectedSlide, setSelectedSlide] = useState(0);

  const currentEmblaApi = emblaApi[selected];

  useEffect(() => {
    if (currentEmblaApi === undefined) {
      return;
    }

    const subscription = currentEmblaApi.on('select', () =>
      setSelectedSlide(currentEmblaApi.selectedScrollSnap()),
    );

    return () => subscription.clear();
  }, [currentEmblaApi, selected]);

  useEffect(() => {
    if (currentEmblaApi === undefined) {
      return;
    }

    currentEmblaApi.scrollTo(selectedSlide);
  }, [selected, selectedSlide, currentEmblaApi]);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={outerWrapper}
    >
      <section className={personsInnerWrapper}>
        <PrismicRichText
          components={{ paragraph: ({ children }) => <h2>{children}</h2> }}
          field={slice.primary.title}
        />
        <Tabs
          data={Object.keys(groups)}
          label={(el) => el}
          onSelect={setSelected}
          selected={selected}
          value={(el) => el}
        />
        <TabPanels
          data={Object.entries(groups)}
          selected={selected}
          value={([group]) => group}
          classNames={{ panels: personGroups, panel: personGroup }}
        >
          {([key, group]) => (
            <React.Fragment key={key}>
              <DefaultCarousel
                options={{ align: 'center' }}
                plugins={[WheelGesturesPlugin()]}
                classNames={{
                  carousel: personGroupCarousel,
                  slides: personGroupSlides,
                }}
                emblaApiRef={(api) =>
                  setEmblaApi((current) =>
                    current[key] !== api ? { ...current, [key]: api } : current,
                  )
                }
              >
                {group.map((person, index) => (
                  <section key={index} className={personSlide}>
                    <PrismicNextImage
                      fallbackAlt=""
                      className={personSlideImg}
                      field={person.image}
                    />
                    <PrismicRichText
                      components={{
                        paragraph: ({ children }) => (
                          <span className={personSlideTitle}>{children}</span>
                        ),
                      }}
                      field={person.title}
                    />
                    <PrismicRichText
                      components={{
                        paragraph: ({ children }) => (
                          <span className={personSlideDescription}>
                            {children}
                          </span>
                        ),
                      }}
                      field={person.description}
                    />
                  </section>
                ))}
              </DefaultCarousel>
              <section>
                <CarouselNav
                  bullets={group.length}
                  selected={selectedSlide}
                  onSelect={setSelectedSlide}
                />
              </section>
            </React.Fragment>
          )}
        </TabPanels>
      </section>
    </section>
  );
}

function TestimoniesCarousel({
  slice,
  sliceType,
}: {
  sliceType: string;
  slice: Content.CarouselSliceTestimonies;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [detailsEmblaRef, detailsEmblaApi] = useEmblaCarousel({});

  const [thumbsEmblaRef, thumbsEmblaApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const [videosEmblaRef, videosEmblaApi] = useEmblaCarousel({}, [Fade()]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!detailsEmblaApi || !thumbsEmblaApi || !videosEmblaApi) {
        return;
      }
      detailsEmblaApi.scrollTo(index);
    },
    [detailsEmblaApi, thumbsEmblaApi, videosEmblaApi],
  );

  const onSelect = useCallback(() => {
    if (!detailsEmblaApi || !thumbsEmblaApi || !videosEmblaApi) {
      return;
    }
    setSelectedIndex(detailsEmblaApi.selectedScrollSnap());
    thumbsEmblaApi.scrollTo(detailsEmblaApi.selectedScrollSnap());
    videosEmblaApi.scrollTo(detailsEmblaApi.selectedScrollSnap());
  }, [detailsEmblaApi, thumbsEmblaApi, videosEmblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!detailsEmblaApi) {
      return;
    }
    onSelect();

    detailsEmblaApi.on('select', onSelect).on('reInit', onSelect);
  }, [detailsEmblaApi, onSelect]);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={slice.variation}
      className={outerWrapper}
    >
      <section className={testimoniesInnerWrapper}>
        <PrismicRichText
          components={{
            paragraph: ({ children }) => (
              <h2 className={testimoniesTitle}>{children}</h2>
            ),
          }}
          field={slice.primary.title}
        />

        <section className={testimoniesDetails}>
          <section className={testimoniesDetailsCarousel} ref={detailsEmblaRef}>
            <section className={testimoniesDetailsSlides}>
              {slice.primary.slides.map((slide, index) => (
                <section key={index} className={testimoniesDetailsSlide}>
                  <PrismicRichText
                    components={{
                      paragraph: ({ children }) => (
                        <p className={testimoniesDetailsSlideTestimony}>
                          {children}
                        </p>
                      ),
                    }}
                    field={slide.testimony}
                  />
                  <span className={testimoniesDetailsSlidePerson}>
                    {slide.person}
                  </span>
                  <span className={testimoniesDetailsSlidePersonTitle}>
                    {slide.person_title}
                  </span>
                </section>
              ))}
            </section>
          </section>
          <section className={testimoniesDetailsThumbCarouselWrapper}>
            <button
              className={testimoniesDetailsThumbCarouselButton}
              disabled={selectedIndex === 0}
              onClick={() => onThumbClick(selectedIndex - 1)}
            >
              <IconBigArrowLeft />
            </button>
            <section
              className={testimoniesDetailsThumbCarousel}
              ref={thumbsEmblaRef}
            >
              <section className={testimoniesDetailsThumbs}>
                {slice.primary.slides.map((slide, index) => (
                  <section
                    key={index}
                    className={clsx(testimoniesDetailsThumb, {
                      [testimoniesDetailsThumbSelected]:
                        selectedIndex === index,
                    })}
                    onClick={() => onThumbClick(index)}
                  >
                    <PrismicNextImage
                      fallbackAlt=""
                      className={testimoniesDetailsThumbImg}
                      field={slide.image}
                    />
                  </section>
                ))}
              </section>
            </section>
            <button
              className={testimoniesDetailsThumbCarouselButton}
              disabled={selectedIndex === slice.primary.slides.length - 1}
              onClick={() => onThumbClick(selectedIndex + 1)}
            >
              <IconBigArrowRight />
            </button>
          </section>
        </section>

        <section className={testimoniesVideos} ref={videosEmblaRef}>
          <section className={testimoniesVideosSlides}>
            {slice.primary.slides.map((slide, index) => (
              <VideoPlayer
                key={index}
                field={slide.video}
                image={slide.video_thumbnail}
                classNames={{ wrapper: testimoniesVideosSlide }}
              />
            ))}
          </section>
        </section>
      </section>
    </section>
  );
}

export default Carousel;
